<template>
  <section class="editor-panel" aria-labelledby="structure-editor-title">
    <header class="editor-heading">
      <div>
        <span class="eyebrow">{{ item ? 'Editar' : 'Adicionar' }}</span>
        <h2 id="structure-editor-title">{{ title }}</h2>
        <p>Campos marcados com * são obrigatórios. Toda alteração fica registrada na auditoria.</p>
      </div>
      <button type="button" class="btn btn-secondary" :disabled="saving" @click="$emit('close')">Fechar</button>
    </header>

    <nav v-if="type === 'function'" class="editor-tabs" aria-label="Seções da função">
      <button v-for="tab in tabs" :key="tab.id" type="button" :class="{ active: activeTab === tab.id }"
        :aria-current="activeTab === tab.id ? 'page' : undefined" @click="selectTab(tab.id)">
        {{ tab.label }}
      </button>
    </nav>

    <form v-if="activeTab === 'general'" @submit.prevent="submitGeneral">
      <div class="form-grid">
        <label>
          <span>Nome *</span>
          <input v-model.trim="form.name" required :maxlength="type === 'sector' ? 45 : type === 'department' ? 85 : 255"
            autocomplete="off">
        </label>

        <label v-if="type !== 'function'">
          <span>Código manual *</span>
          <input v-model.trim="form.code" required maxlength="20" placeholder="Ex.: 03.02" autocomplete="off">
          <small v-if="!form.code" class="required-help">Informe o código separado do nome para salvar.</small>
        </label>

        <label v-if="type === 'department'">
          <span>Departamento pai</span>
          <select v-model="form.parent_id">
            <option :value="null">Sem pai</option>
            <option v-for="department in availableDepartments" :key="department.id" :value="department.id">
              {{ optionLabel(department) }}
            </option>
          </select>
        </label>

        <label v-if="type === 'department' && !form.parent_id">
          <span>Cliente *</span>
          <input v-model.number="form.client_id" type="number" min="1" required inputmode="numeric">
          <small>Para departamentos filhos, o cliente é herdado do pai.</small>
        </label>

        <label v-if="type === 'sector'">
          <span>Departamento *</span>
          <select v-model="form.department_id" required @change="validateSectorParent">
            <option disabled :value="null">Selecione</option>
            <option v-for="department in activeDepartments" :key="department.id" :value="department.id">
              {{ optionLabel(department) }}
            </option>
          </select>
        </label>

        <label v-if="type === 'sector'">
          <span>Setor pai</span>
          <input v-model="sectorParentSearch" list="sector-parent-options" autocomplete="off"
            placeholder="Digite para localizar" @change="selectSectorParent">
          <datalist id="sector-parent-options">
            <option v-for="sector in availableSectorParents" :key="sector.id" :value="optionLabel(sector)" />
          </datalist>
        </label>

        <label v-if="type === 'function'" class="span-2">
          <span>Setor de lotação *</span>
          <input v-model="functionSectorSearch" list="function-sector-options" required autocomplete="off"
            placeholder="Digite o código ou nome" @change="selectFunctionSector">
          <datalist id="function-sector-options">
            <option v-for="sector in activeSectors" :key="sector.id" :value="sectorFullLabel(sector)" />
          </datalist>
          <small>Alterar a lotação é bloqueado enquanto houver pessoas ativas na função.</small>
        </label>

        <label v-if="type !== 'function'">
          <span>Ordem de exibição</span>
          <input v-model.number="form.display_order" type="number" min="0" inputmode="numeric">
        </label>

        <label v-if="type === 'department' || type === 'function'" class="span-2">
          <span>Caminho organizacional</span>
          <input v-model.trim="form.organizational_path" maxlength="255" autocomplete="off">
          <small>O valor legado é preservado e nunca é recalculado automaticamente.</small>
        </label>

        <label>
          <span>Grupo hierárquico (e-mail Google)</span>
          <input v-model.trim="form.hierarchical_group_email" type="email" maxlength="255" inputmode="email" autocomplete="off">
          <small>Grupo que representa este item na hierarquia do Google Workspace.</small>
        </label>

        <label>
          <span>Grupo de comunicação (e-mail Google)</span>
          <input v-model.trim="form.communication_group_email" type="email" maxlength="255" inputmode="email" autocomplete="off">
          <small>Grupo usado para as comunicações deste item.</small>
        </label>

        <template v-if="type === 'function'">
          <label>
            <span>URL inicial</span>
            <input v-model.trim="form.home_url" maxlength="255" autocomplete="url">
          </label>
          <div class="check-group" role="group" aria-label="Características da função">
            <label class="check-option"><input v-model="form.is_teacher" type="checkbox"> Professor</label>
          </div>
        </template>
      </div>

      <label class="reason-field">
        <span>Motivo da alteração *</span>
        <textarea v-model.trim="form.reason" required minlength="3" maxlength="500" rows="3"
          placeholder="Descreva objetivamente por que esta alteração é necessária" />
      </label>

      <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
      <footer class="form-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="$emit('close')">Cancelar</button>
        <button type="submit" class="btn btn-primary" :disabled="saving || item?.status === 'inactive'">
          {{ saving ? 'Salvando...' : item ? 'Salvar alterações' : type === 'function' ? 'Continuar' : `Criar ${typeLabel.toLowerCase()}` }}
        </button>
      </footer>
    </form>

    <section v-else-if="activeTab === 'responsibilities'" aria-labelledby="responsibilities-title">
      <h3 id="responsibilities-title">Responsabilidades da função</h3>
      <p>A responsabilidade não altera a lotação da função, não move pessoas e não concede permissões.</p>
      <div class="responsibility-grid">
        <div>
          <label><span>Buscar departamentos</span><input v-model.trim="departmentSearch" type="search" autocomplete="off"></label>
          <div class="selection-list">
            <label v-for="department in filteredDepartments" :key="department.id" class="check-option">
              <input v-model="form.department_responsibility_ids" type="checkbox" :value="department.id">
              <span>{{ optionLabel(department) }}</span>
            </label>
          </div>
        </div>
        <div>
          <label><span>Buscar áreas e subáreas</span><input v-model.trim="sectorSearch" type="search" autocomplete="off"></label>
          <div class="selection-list">
            <label v-for="sector in filteredSectors" :key="sector.id" class="check-option">
              <input v-model="form.sector_responsibility_ids" type="checkbox" :value="sector.id">
              <span>{{ sectorFullLabel(sector) }}</span>
            </label>
          </div>
        </div>
      </div>
      <p class="selection-summary">{{ responsibilityCount }} responsabilidade(s) selecionada(s).</p>
      <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
      <footer class="form-actions">
        <button type="button" class="btn btn-secondary" @click="activeTab = 'general'">Voltar</button>
        <button type="button" class="btn btn-primary" :disabled="saving || !canSave" @click="submitResponsibilities">
          {{ saving ? 'Salvando...' : item ? 'Salvar responsabilidades' : 'Continuar para permissões' }}
        </button>
      </footer>
    </section>

    <section v-else-if="activeTab === 'permissions'" class="permissions-panel" aria-labelledby="permissions-title">
      <h3 id="permissions-title">{{ item ? 'Gerenciar permissões' : 'Permissões iniciais' }}</h3>
      <p v-if="item">Escolha permissões específicas ou replique o conjunto de outra função. As duas ações substituem os vínculos atuais de <strong>{{ form.name }}</strong>.</p>
      <p v-else>Escolha uma função existente para copiar suas permissões. A lotação, as responsabilidades e os demais dados não são copiados.</p>

      <div v-if="permissionsLoading" class="local-loading" aria-live="polite">Carregando permissões...</div>
      <template v-else>
        <p v-if="item" class="permission-count">A função possui atualmente <strong>{{ permissions.length }}</strong> vínculo(s) de permissão.</p>
        <div v-if="item" class="permission-modes" role="group" aria-label="Forma de atribuição das permissões">
          <button type="button" :class="{ active: permissionMode === 'select' }"
            :aria-pressed="permissionMode === 'select'" @click="selectPermissionMode('select')">
            Selecionar permissões
          </button>
          <button type="button" :class="{ active: permissionMode === 'clone' }"
            :aria-pressed="permissionMode === 'clone'" @click="selectPermissionMode('clone')">
            Clonar de outra função
          </button>
        </div>

        <template v-if="item && permissionMode === 'select'">
          <div class="permission-dual-list">
            <section class="permission-column" aria-labelledby="available-permissions-title">
              <header>
                <strong id="available-permissions-title">{{ availablePermissionOptions.length }} permissões disponíveis</strong>
                <button type="button" class="btn btn-secondary" :disabled="!availablePermissionOptions.length"
                  @click="addAllPermissions">Adicionar tudo</button>
              </header>
              <label>
                <span class="sr-only">Buscar nas permissões disponíveis</span>
                <input v-model.trim="availablePermissionSearch" type="search" autocomplete="off" placeholder="Buscar permissão disponível...">
              </label>
              <div class="permission-transfer-list" role="list" aria-label="Permissões disponíveis">
                <button v-for="permission in filteredAvailablePermissions" :key="permission.id" type="button"
                  class="permission-row" :aria-label="`Adicionar permissão ${permission.name}`" @click="addPermission(permission.id)">
                  <span><strong>{{ permission.name }}</strong><small v-if="permission.roleName">{{ permission.roleName }}</small></span>
                  <span class="transfer-action" aria-hidden="true">Adicionar</span>
                </button>
                <p v-if="!filteredAvailablePermissions.length" class="permission-empty">Nenhuma permissão disponível.</p>
              </div>
            </section>

            <div class="permission-transfer-indicator" aria-hidden="true">⇄</div>

            <section class="permission-column" aria-labelledby="selected-permissions-title">
              <header>
                <strong id="selected-permissions-title">{{ selectedPermissionIds.length }} selecionadas</strong>
                <button type="button" class="btn btn-secondary" :disabled="!selectedPermissionIds.length"
                  @click="removeAllPermissions">Remover tudo</button>
              </header>
              <label>
                <span class="sr-only">Buscar nas permissões selecionadas</span>
                <input v-model.trim="selectedPermissionSearch" type="search" autocomplete="off" placeholder="Buscar selecionadas...">
              </label>
              <div class="permission-transfer-list" role="list" aria-label="Permissões selecionadas">
                <div v-for="permission in filteredSelectedPermissions" :key="permission.id" class="permission-row selected">
                  <button type="button" class="permission-name" :aria-label="`Remover permissão ${permission.name}`"
                    @click="removePermission(permission.id)">
                    <span><strong>{{ permission.name }}</strong><small v-if="permission.roleName">{{ permission.roleName }}</small></span>
                  </button>
                  <button type="button" class="permission-home" :class="{ active: isHomePermission(permission.id) }"
                    :aria-pressed="isHomePermission(permission.id)" @click="togglePermissionHome(permission.id)">
                    {{ isHomePermission(permission.id) ? 'Remover da home' : 'Adicionar à home' }}
                    <fa :icon="['far', 'house']" aria-hidden="true" />
                  </button>
                </div>
                <p v-if="!filteredSelectedPermissions.length" class="permission-empty">Nenhuma permissão selecionada.</p>
              </div>
            </section>
          </div>
          <label class="reason-field">
            <span>Motivo da alteração *</span>
            <textarea v-model.trim="permissionReason" minlength="3" maxlength="500" rows="3"
              placeholder="Descreva por que este conjunto de permissões deve ser atribuído" />
          </label>
          <label class="check-option confirmation">
            <input v-model="permissionConfirmed" type="checkbox">
            Confirmo que as permissões atuais serão substituídas pela seleção acima.
          </label>
          <p v-if="!hasPermissionChanges" class="permission-hint">Altere a seleção para habilitar o salvamento.</p>
        </template>

        <template v-else>
          <label>
            <span>Função de origem {{ item ? '*' : '(opcional)' }}</span>
            <input v-model.trim="functionSearch" type="search" autocomplete="off" placeholder="Digite para localizar"
              @input="sourceFunctionId = null">
          </label>
          <div v-if="functionSearch" class="source-results" role="listbox" aria-label="Funções encontradas">
            <button v-for="source in filteredSourceFunctions" :key="source.id" type="button"
              role="option" :aria-selected="sourceFunctionId === source.id"
              :class="{ selected: sourceFunctionId === source.id }" @click="selectSourceFunction(source)">
              <span>{{ source.name }}</span><small>{{ source.permissionCount }} permissões · {{ source.sectorName }}</small>
            </button>
            <p v-if="!filteredSourceFunctions.length">Nenhuma função encontrada.</p>
          </div>

          <label v-if="sourceFunctionId" class="reason-field">
            <span>Motivo da clonagem *</span>
            <textarea v-model.trim="cloneReason" minlength="3" maxlength="500" rows="3"
              placeholder="Descreva por que esta função deve receber as mesmas permissões" />
          </label>
          <label v-if="sourceFunctionId" class="check-option confirmation">
            <input v-model="cloneConfirmed" type="checkbox">
            {{ item ? 'Confirmo que as permissões atuais da função destino serão substituídas.' : 'Confirmo que a nova função receberá as mesmas permissões da função selecionada.' }}
          </label>
          <p v-if="!item && !sourceFunctionId" class="permission-hint">Se nenhuma origem for selecionada, a função será criada sem permissões.</p>
          <p v-if="!item && functionSearch && !sourceFunctionId" class="permission-hint">Selecione uma função da lista ou limpe a busca para continuar sem clonar.</p>
          <p v-if="!item && !canSave" class="permission-hint">Preencha os campos obrigatórios em Dados gerais antes de criar a função.</p>
        </template>
        <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
        <footer class="form-actions">
          <button type="button" class="btn btn-secondary" @click="activeTab = item ? 'general' : 'responsibilities'">Voltar</button>
          <button type="button" class="btn btn-primary" :disabled="!canSubmitPermissions" @click="submitPermissions">
            {{ permissionActionLabel }}
          </button>
        </footer>
      </template>
    </section>

    <section v-else class="permissions-panel" aria-labelledby="activity-permissions-title">
      <h3 id="activity-permissions-title">{{ item ? 'Gerenciar permissões de atividades' : 'Permissões iniciais de atividades' }}</h3>
      <p v-if="item">Escolha uma função para substituir as permissões de atividades de <strong>{{ form.name }}</strong>. A clonagem preserva os acessos de visualização, edição, execução e gerenciamento.</p>
      <p v-else>Escolha uma função existente para copiar suas permissões de atividades. As permissões gerais e os demais dados não são copiados nesta etapa.</p>

      <p v-if="item" class="permission-count">A função possui atualmente <strong>{{ item.activityPermissionCount || 0 }}</strong> categoria(s) de atividade com acesso.</p>
      <label>
        <span>Função de origem {{ item ? '*' : '(opcional)' }}</span>
        <input v-model.trim="activityFunctionSearch" type="search" autocomplete="off" placeholder="Digite para localizar"
          @input="activitySourceFunctionId = null">
      </label>
      <div v-if="activityFunctionSearch" class="source-results" role="listbox" aria-label="Funções encontradas para permissões de atividades">
        <button v-for="source in filteredActivitySourceFunctions" :key="source.id" type="button"
          role="option" :aria-selected="activitySourceFunctionId === source.id"
          :class="{ selected: activitySourceFunctionId === source.id }" @click="selectActivitySourceFunction(source)">
          <span>{{ source.name }}</span><small>{{ source.activityPermissionCount }} permissões de atividades · {{ source.sectorName }}</small>
        </button>
        <p v-if="!filteredActivitySourceFunctions.length">Nenhuma função com permissões de atividades foi encontrada.</p>
      </div>

      <label v-if="activitySourceFunctionId" class="reason-field">
        <span>Motivo da clonagem *</span>
        <textarea v-model.trim="activityCloneReason" minlength="3" maxlength="500" rows="3"
          placeholder="Descreva por que esta função deve receber as mesmas permissões de atividades" />
      </label>
      <label v-if="activitySourceFunctionId" class="check-option confirmation">
        <input v-model="activityCloneConfirmed" type="checkbox">
        {{ item ? 'Confirmo que as permissões de atividades atuais da função destino serão substituídas.' : 'Confirmo que a nova função receberá as mesmas permissões de atividades da função selecionada.' }}
      </label>
      <p v-if="!item && !activitySourceFunctionId" class="permission-hint">Se nenhuma origem for selecionada, a função será criada sem permissões de atividades.</p>
      <p v-if="!item && activityFunctionSearch && !activitySourceFunctionId" class="permission-hint">Selecione uma função da lista ou limpe a busca para continuar sem clonar.</p>
      <p v-if="!item && !canSave" class="permission-hint">Preencha os campos obrigatórios em Dados gerais antes de criar a função.</p>
      <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
      <footer class="form-actions">
        <button type="button" class="btn btn-secondary" @click="activeTab = 'permissions'">Voltar</button>
        <button type="button" class="btn btn-primary" :disabled="!canSubmitActivityPermissions" @click="submitActivityPermissions">
          {{ activityPermissionActionLabel }}
        </button>
      </footer>
    </section>

    <aside v-if="item" class="status-actions" :class="item.status">
      <div>
        <strong>{{ item.status === 'active' ? 'Item ativo' : 'Item inativo' }}</strong>
        <p>{{ item.status === 'active' ? 'A inativação é bloqueada quando existem dependências ativas.' : 'A restauração exige que o item pai esteja ativo.' }}</p>
      </div>
      <div class="status-confirmation">
        <label class="check-option">
          <input v-model="statusConfirmed" type="checkbox">
          <span>Confirmo a alteração de status usando o motivo informado acima.</span>
        </label>
        <button type="button" class="btn btn-secondary" :disabled="saving || !statusConfirmed || form.reason.length < 3" @click="changeStatus">
          {{ item.status === 'active' ? 'Inativar' : 'Restaurar' }}
        </button>
      </div>
    </aside>
  </section>
</template>

<script>
import OrganizationalStructureService from '@/services/organizational-structure.service'

export default {
  name: 'StructureEditor',
  props: {
    type: { type: String, required: true },
    item: { type: Object, default: null },
    catalog: { type: Object, required: true }
  },
  emits: ['close', 'saved'],
  data () {
    return {
      activeTab: 'general',
      saving: false,
      error: '',
      form: this.initialForm(),
      sectorParentSearch: '',
      functionSectorSearch: '',
      departmentSearch: '',
      sectorSearch: '',
      permissions: [],
      availablePermissions: [],
      permissionsLoading: false,
      permissionMode: this.item ? 'select' : 'clone',
      availablePermissionSearch: '',
      selectedPermissionSearch: '',
      selectedPermissionIds: [],
      homePermissionIds: [],
      permissionReason: '',
      permissionConfirmed: false,
      functionSearch: '',
      sourceFunctionId: null,
      cloneReason: '',
      cloneConfirmed: false,
      activityFunctionSearch: '',
      activitySourceFunctionId: null,
      activityCloneReason: '',
      activityCloneConfirmed: false,
      statusConfirmed: false
    }
  },
  computed: {
    tabs () {
      if (this.item?.status === 'inactive') return [{ id: 'general', label: 'Dados gerais' }]
      return [
        { id: 'general', label: 'Dados gerais' },
        { id: 'responsibilities', label: 'Responsabilidades' },
        { id: 'permissions', label: 'Permissões' },
        { id: 'activityPermissions', label: 'Permissões de atividades' }
      ]
    },
    title () {
      return `${this.item ? 'Editar' : 'Adicionar'} ${this.typeLabel.toLowerCase()}`
    },
    typeLabel () {
      return { department: 'Departamento', sector: 'Setor', function: 'Função' }[this.type]
    },
    activeDepartments () {
      return this.catalog.departments.filter(item => item.status === 'active')
    },
    activeSectors () {
      return this.catalog.sectors.filter(item => item.status === 'active')
    },
    availableDepartments () {
      return this.activeDepartments.filter(item => !this.item || item.id !== this.item.id)
    },
    availableSectorParents () {
      return this.activeSectors.filter(item => item.departmentId === Number(this.form.department_id) && (!this.item || item.id !== this.item.id))
    },
    filteredDepartments () {
      return this.activeDepartments.filter(item => this.matches(item, this.departmentSearch))
    },
    filteredSectors () {
      return this.activeSectors.filter(item => this.matches({ ...item, name: this.sectorFullLabel(item) }, this.sectorSearch))
    },
    filteredSourceFunctions () {
      return this.catalog.functions
        .filter(item => item.status === 'active' && item.id !== this.item?.id && this.matches(item, this.functionSearch))
        .slice(0, 8)
    },
    filteredActivitySourceFunctions () {
      return this.catalog.functions
        .filter(item => item.status === 'active' && item.id !== this.item?.id && item.activityPermissionCount > 0 && this.matches(item, this.activityFunctionSearch))
        .slice(0, 8)
    },
    permissionOptions () {
      const permissionsById = new Map(this.availablePermissions.map(permission => [permission.id, permission]))
      this.permissions.forEach(permission => {
        if (!permissionsById.has(permission.id)) permissionsById.set(permission.id, permission)
      })
      return [...permissionsById.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    },
    availablePermissionOptions () {
      return this.permissionOptions.filter(permission => !this.selectedPermissionIds.includes(Number(permission.id)))
    },
    selectedPermissionOptions () {
      return this.permissionOptions.filter(permission => this.selectedPermissionIds.includes(Number(permission.id)))
    },
    filteredAvailablePermissions () {
      const search = this.normalize(this.availablePermissionSearch)
      return this.availablePermissionOptions.filter(permission => !search || this.matchesPermission(permission, search))
    },
    filteredSelectedPermissions () {
      const search = this.normalize(this.selectedPermissionSearch)
      return this.selectedPermissionOptions.filter(permission => !search || this.matchesPermission(permission, search))
    },
    hasPermissionChanges () {
      const current = [...new Set(this.permissions.map(permission => Number(permission.id)))].sort((a, b) => a - b)
      const selected = [...new Set(this.selectedPermissionIds.map(Number))].sort((a, b) => a - b)
      const currentHome = [...new Set(this.permissions.filter(permission => permission.home).map(permission => Number(permission.id)))].sort((a, b) => a - b)
      const selectedHome = [...new Set(this.homePermissionIds.map(Number))].sort((a, b) => a - b)
      const selectionChanged = current.length !== selected.length || current.some((id, index) => id !== selected[index])
      const homeChanged = currentHome.length !== selectedHome.length || currentHome.some((id, index) => id !== selectedHome[index])
      return selectionChanged || homeChanged
    },
    responsibilityCount () {
      return this.form.department_responsibility_ids.length + this.form.sector_responsibility_ids.length
    },
    groupEmailsValid () {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return [this.form.hierarchical_group_email, this.form.communication_group_email].every(email => !email || emailPattern.test(email))
    },
    canSave () {
      if (this.item?.status === 'inactive') return false
      if (this.form.reason.length < 3 || !this.form.name || !this.groupEmailsValid) return false
      if (this.type === 'department') return Boolean(this.form.code && (this.form.parent_id || this.form.client_id))
      if (this.type === 'sector') return Boolean(this.form.code && this.form.department_id)
      return Boolean(this.form.sector_id)
    },
    canSubmitPermissions () {
      if (this.saving) return false
      if (!this.item && !this.canSave) return false
      if (!this.item && this.functionSearch && !this.sourceFunctionId) return false
      if (this.item && this.permissionMode === 'select') {
        return this.hasPermissionChanges && this.permissionReason.length >= 3 && this.permissionConfirmed
      }
      if (this.item && !this.sourceFunctionId) return false
      return !this.sourceFunctionId || (this.cloneReason.length >= 3 && this.cloneConfirmed)
    },
    canSubmitActivityPermissions () {
      if (this.saving) return false
      if (!this.item && !this.canSave) return false
      if (!this.item && this.activityFunctionSearch && !this.activitySourceFunctionId) return false
      if (this.item && !this.activitySourceFunctionId) return false
      return !this.activitySourceFunctionId || (this.activityCloneReason.length >= 3 && this.activityCloneConfirmed)
    },
    permissionActionLabel () {
      if (this.saving) return this.item && this.permissionMode === 'select' ? 'Salvando...' : this.item ? 'Clonando...' : 'Criando...'
      if (this.item) return this.permissionMode === 'select' ? 'Salvar permissões' : 'Clonar permissões'
      return 'Continuar para permissões de atividades'
    },
    activityPermissionActionLabel () {
      if (this.saving) return this.item ? 'Clonando...' : 'Criando...'
      if (this.item) return 'Clonar permissões de atividades'
      if (this.sourceFunctionId || this.activitySourceFunctionId) return 'Criar função e clonar permissões'
      return 'Criar sem permissões'
    }
  },
  mounted () {
    this.sectorParentSearch = this.item?.parentName ? this.optionLabel({ code: this.item.code, name: this.item.parentName }) : ''
    if (this.type === 'sector' && this.item?.parentId) {
      const parent = this.catalog.sectors.find(item => item.id === this.item.parentId)
      this.sectorParentSearch = parent ? this.optionLabel(parent) : ''
    }
    if (this.type === 'function' && this.form.sector_id) {
      const sector = this.catalog.sectors.find(item => item.id === this.form.sector_id)
      this.functionSectorSearch = sector ? this.sectorFullLabel(sector) : ''
    }
  },
  methods: {
    initialForm () {
      return {
        name: this.item?.name || '',
        code: this.item?.code || '',
        parent_id: this.item?.parentId || null,
        client_id: this.item?.clientId || null,
        department_id: this.item?.departmentId || null,
        sector_id: this.item?.sectorId || null,
        display_order: this.item?.displayOrder || 0,
        organizational_path: this.item?.organizationalPath || '',
        home_url: this.item?.homeUrl || '',
        hierarchical_group_email: this.item?.hierarchicalGroupEmail || '',
        communication_group_email: this.item?.communicationGroupEmail || '',
        is_teacher: Boolean(this.item?.isTeacher),
        department_responsibility_ids: [...(this.item?.departmentResponsibilityIds || [])],
        sector_responsibility_ids: [...(this.item?.sectorResponsibilityIds || [])],
        reason: ''
      }
    },
    optionLabel (item) {
      return `${item.code ? `${item.code} · ` : ''}${item.name}`
    },
    sectorFullLabel (sector) {
      return `${sector.code ? `${sector.code} · ` : ''}${sector.name} — ${sector.departmentName}`
    },
    normalize (value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    },
    matches (item, search) {
      const term = this.normalize(search)
      return !term || this.normalize(`${item.code || ''} ${item.name} ${item.sectorName || ''}`).includes(term)
    },
    validateSectorParent () {
      const parent = this.catalog.sectors.find(item => item.id === Number(this.form.parent_id))
      if (parent && parent.departmentId !== Number(this.form.department_id)) {
        this.form.parent_id = null
        this.sectorParentSearch = ''
      }
    },
    selectSectorParent () {
      const match = this.availableSectorParents.find(item => this.sectorParentSearch === this.optionLabel(item))
      this.form.parent_id = match?.id || null
      if (!match && this.sectorParentSearch) this.error = 'Selecione um setor pai da lista.'
    },
    selectFunctionSector () {
      const match = this.activeSectors.find(item => this.functionSectorSearch === this.sectorFullLabel(item))
      this.form.sector_id = match?.id || null
      if (!match && this.functionSectorSearch) this.error = 'Selecione um setor de lotação da lista.'
    },
    async selectTab (tab) {
      this.activeTab = tab
      this.error = ''
      if (tab === 'permissions' && this.item && !this.permissions.length) await this.loadPermissions()
    },
    submitGeneral () {
      if (this.type !== 'function' || this.item) {
        this.save()
        return
      }
      if (!this.canSave) {
        this.error = 'Preencha os campos obrigatórios e informe o motivo.'
        return
      }
      this.error = ''
      this.activeTab = 'responsibilities'
    },
    submitResponsibilities () {
      if (this.item) {
        this.save()
        return
      }
      if (!this.canSave) {
        this.error = 'Preencha os campos obrigatórios e informe o motivo.'
        return
      }
      this.error = ''
      this.activeTab = 'permissions'
    },
    selectSourceFunction (source) {
      this.sourceFunctionId = source.id
      this.functionSearch = source.name
      this.cloneReason = ''
      this.cloneConfirmed = false
    },
    selectActivitySourceFunction (source) {
      this.activitySourceFunctionId = source.id
      this.activityFunctionSearch = source.name
      this.activityCloneReason = ''
      this.activityCloneConfirmed = false
    },
    selectPermissionMode (mode) {
      this.permissionMode = mode
      this.error = ''
    },
    matchesPermission (permission, normalizedSearch) {
      return this.normalize(`${permission.name} ${permission.roleName || ''}`).includes(normalizedSearch)
    },
    addAllPermissions () {
      this.selectedPermissionIds = [...new Set([
        ...this.selectedPermissionIds.map(Number),
        ...this.availablePermissionOptions.map(permission => Number(permission.id))
      ])]
    },
    addPermission (permissionId) {
      this.selectedPermissionIds = [...new Set([...this.selectedPermissionIds, Number(permissionId)])]
    },
    removePermission (permissionId) {
      const id = Number(permissionId)
      this.selectedPermissionIds = this.selectedPermissionIds.filter(selectedId => Number(selectedId) !== id)
      this.homePermissionIds = this.homePermissionIds.filter(homeId => Number(homeId) !== id)
    },
    removeAllPermissions () {
      this.selectedPermissionIds = []
      this.homePermissionIds = []
    },
    isHomePermission (permissionId) {
      return this.homePermissionIds.includes(Number(permissionId))
    },
    togglePermissionHome (permissionId) {
      const id = Number(permissionId)
      this.homePermissionIds = this.isHomePermission(id)
        ? this.homePermissionIds.filter(homeId => Number(homeId) !== id)
        : [...this.homePermissionIds, id]
    },
    payload () {
      const common = {
        name: this.form.name,
        reason: this.form.reason,
        expected_updated_at: this.item?.updatedAt || null,
        hierarchical_group_email: this.form.hierarchical_group_email || null,
        communication_group_email: this.form.communication_group_email || null
      }
      if (this.type === 'department') return { ...common, code: this.form.code, parent_id: this.form.parent_id, client_id: this.form.client_id, organizational_path: this.form.organizational_path || null, display_order: this.form.display_order }
      if (this.type === 'sector') return { ...common, code: this.form.code, department_id: this.form.department_id, parent_id: this.form.parent_id, display_order: this.form.display_order }
      const payload = { ...common, sector_id: this.form.sector_id, organizational_path: this.form.organizational_path || null, home_url: this.form.home_url || null, is_teacher: this.form.is_teacher, department_responsibility_ids: this.form.department_responsibility_ids, sector_responsibility_ids: this.form.sector_responsibility_ids }
      if (!this.item && this.sourceFunctionId) {
        payload.permission_source_function_id = this.sourceFunctionId
        payload.permission_clone_reason = this.cloneReason
      }
      if (!this.item && this.activitySourceFunctionId) {
        payload.activity_permission_source_function_id = this.activitySourceFunctionId
        payload.activity_permission_clone_reason = this.activityCloneReason
      }
      return payload
    },
    async save () {
      if (!this.canSave) {
        this.error = 'Preencha os campos obrigatórios e informe o motivo.'
        return
      }
      this.saving = true
      this.error = ''
      try {
        const response = this.item
          ? await OrganizationalStructureService.update(this.type, this.item.id, this.payload())
          : await OrganizationalStructureService.create(this.type, this.payload())
        this.success(response.data.message)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    async loadPermissions () {
      this.permissionsLoading = true
      try {
        const response = await OrganizationalStructureService.permissions(this.item.id)
        this.permissions = response.data.data.permissions || []
        this.availablePermissions = response.data.data.availablePermissions || []
        this.selectedPermissionIds = [...new Set(this.permissions.map(permission => Number(permission.id)))]
        this.homePermissionIds = [...new Set(this.permissions.filter(permission => permission.home).map(permission => Number(permission.id)))]
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.permissionsLoading = false
      }
    },
    async clonePermissions () {
      this.saving = true
      this.error = ''
      try {
        const response = await OrganizationalStructureService.clonePermissions(this.item.id, this.sourceFunctionId, this.cloneReason, this.item.updatedAt)
        this.success(response.data.message)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    async cloneActivityPermissions () {
      this.saving = true
      this.error = ''
      try {
        const response = await OrganizationalStructureService.cloneActivityPermissions(
          this.item.id,
          this.activitySourceFunctionId,
          this.activityCloneReason,
          this.item.updatedAt
        )
        this.success(response.data.message)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    async syncPermissions () {
      this.saving = true
      this.error = ''
      try {
        const response = await OrganizationalStructureService.syncPermissions(
          this.item.id,
          this.selectedPermissionIds.map(Number),
          this.homePermissionIds.map(Number),
          this.permissionReason,
          this.item.updatedAt
        )
        this.success(response.data.message)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    submitPermissions () {
      if (!this.canSubmitPermissions) return
      if (!this.item) {
        this.activeTab = 'activityPermissions'
        return
      }
      if (this.item && this.permissionMode === 'select') this.syncPermissions()
      else if (this.item) this.clonePermissions()
    },
    submitActivityPermissions () {
      if (!this.canSubmitActivityPermissions) return
      if (this.item) this.cloneActivityPermissions()
      else this.save()
    },
    async changeStatus () {
      this.saving = true
      this.error = ''
      try {
        const response = this.item.status === 'active'
          ? await OrganizationalStructureService.deactivate(this.type, this.item.id, this.form.reason, this.item.updatedAt)
          : await OrganizationalStructureService.restore(this.type, this.item.id, this.form.reason, this.item.updatedAt)
        this.success(response.data.message)
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    success (message) {
      window.toaster.emit('open', { style: 'success', message, floater: true })
      this.$emit('saved')
    },
    errorMessage (error) {
      const validation = error.response?.data?.errors
      if (validation) return Object.values(validation).flat()[0]
      return error.response?.data?.message || 'Não foi possível concluir a operação.'
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-panel { margin-bottom: $mg_mid; padding: $hmg_mid; border: $border-component; border-radius: $border-radius; background: $color-secondary; }
.editor-heading { display: flex; justify-content: space-between; gap: $mg_small; align-items: flex-start; }
.editor-heading h2 { margin: $hmg_mini 0; font-size: 20px; }
.editor-heading p, section > p { margin: 0; color: $color-text; }
.eyebrow { color: $color-primary; font-size: $font-size-small; font-weight: 700; text-transform: uppercase; }
.editor-tabs { display: flex; gap: $mg_mini; margin: $hmg_mid 0; border-bottom: $border-component; overflow-x: auto; }
.editor-tabs button { min-height: 44px; padding: 0 $mg_small; border: 0; border-bottom: 3px solid transparent; background: transparent; font-weight: 600; cursor: pointer; white-space: nowrap; }
.editor-tabs button.active { border-bottom-color: $color-primary; color: $color-primary; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: $mg_small; margin-top: $hmg_mid; }
label { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
label small { color: $color-text; font-weight: 400; }
.required-help { color: $color-primary; }
input, select, textarea { width: 100%; min-height: 44px; padding: $mg_mini $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
textarea { resize: vertical; }
input:focus-visible, select:focus-visible, textarea:focus-visible, button:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.span-2, .reason-field { grid-column: 1 / -1; }
.reason-field { margin-top: $mg_small; }
.check-group { display: flex; align-items: center; gap: $mg_small; padding-top: 22px; }
.check-option { display: flex; flex-direction: row; align-items: center; gap: $mg_mini; min-width: 0; min-height: 44px; font-weight: 500; cursor: pointer; }
.check-option input { flex: 0 0 20px; width: 20px; height: 20px; min-height: 20px; margin: 0; padding: 0; accent-color: $color-primary; cursor: pointer; }
.check-option span { min-width: 0; line-height: 1.4; overflow-wrap: break-word; }
.form-actions { display: flex; justify-content: flex-end; gap: $mg_small; margin-top: $hmg_mid; }
.inline-error { margin: $mg_small 0 0; padding: $mg_small; border-left: 4px solid $color-error; background: $color-bg; color: $color-dev; }
.responsibility-grid { display: grid; grid-template-columns: 1fr 1fr; gap: $mg_small; margin-top: $mg_small; }
.selection-list { display: grid; max-height: 300px; margin-top: $mg_mini; padding: $hmg_mini; border: $border-component; border-radius: $border-radius-mid; overflow-y: auto; }
.selection-list .check-option { width: 100%; padding: 0 $mg_mini; border-radius: $border-radius-mid; }
.selection-list .check-option:hover { background: $color-bg; }
.selection-list .check-option + .check-option { border-top: $border-component; }
.selection-summary, .permission-count { margin-top: $mg_small; }
.permission-modes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); max-width: 560px; margin: $mg_small 0; padding: 4px; border: $border-component; border-radius: $border-radius-mid; background: $color-bg; }
.permission-modes button { min-height: 44px; padding: $mg_mini $mg_small; border: 0; border-radius: $border-radius-mid; background: transparent; color: $color-dev; font-weight: 600; cursor: pointer; }
.permission-modes button.active { background: $color-secondary; color: $color-primary; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }
.permission-dual-list { display: grid; grid-template-columns: minmax(0, 1fr) 32px minmax(0, 1fr); gap: $mg_small; align-items: center; margin-top: $mg_small; }
.permission-column { min-width: 0; padding: $mg_small; border: $border-component; border-radius: $border-radius; background: $color-secondary; }
.permission-column header { display: flex; min-height: 44px; align-items: center; justify-content: space-between; gap: $mg_small; margin-bottom: $mg_mini; }
.permission-column header .btn { min-height: 40px; white-space: nowrap; }
.permission-transfer-list { display: grid; align-content: start; max-height: 360px; min-height: 280px; margin-top: $mg_mini; padding: $hmg_mini; border: $border-component; border-radius: $border-radius-mid; overflow-y: auto; }
.permission-row { display: flex; width: 100%; min-height: 52px; align-items: center; justify-content: space-between; gap: $mg_small; padding: $mg_mini $mg_small; border: 0; border-radius: $border-radius-mid; background: $color-bg; color: $color-dev; text-align: left; }
button.permission-row { cursor: pointer; }
.permission-row + .permission-row { margin-top: $hmg_mini; }
.permission-row:hover, .permission-row:focus-within { background: darken($color-bg, 2%); }
.permission-row > span, .permission-name span { display: flex; min-width: 0; flex-direction: column; }
.permission-row strong { overflow-wrap: anywhere; }
.permission-row small { color: $color-text; font-weight: 400; }
.transfer-action { flex: 0 0 auto; color: $color-primary; font-weight: 600; }
.permission-row.selected { padding-right: $mg_mini; }
.permission-name, .permission-home { min-height: 44px; border: 0; background: transparent; color: $color-dev; cursor: pointer; }
.permission-name { display: flex; min-width: 0; flex: 1; align-items: center; padding: 0; text-align: left; }
.permission-home { display: inline-flex; flex: 0 0 auto; align-items: center; gap: $hmg_mini; padding: 0 $mg_mini; color: $color-text; font-weight: 600; white-space: nowrap; }
.permission-home.active { color: $color-primary; }
.permission-transfer-indicator { color: $color-text; font-size: 20px; text-align: center; }
.permission-empty { margin: auto; padding: $mg_small; color: $color-text; text-align: center; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.source-results { display: grid; gap: $hmg_mini; max-height: 320px; margin-top: $hmg_mini; overflow-y: auto; }
.source-results button { display: flex; flex-direction: column; min-height: 54px; padding: $mg_mini $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; text-align: left; cursor: pointer; }
.source-results button.selected { border-color: $color-primary; color: $color-primary; }
.source-results small { color: $color-text; }
.confirmation { margin-top: $mg_small; }
.permission-hint { margin: $mg_small 0 0; color: $color-text; }
.status-actions { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: $mg_small; align-items: center; margin-top: $mg_mid; padding: $mg_small; border: $border-component; border-radius: $border-radius-mid; }
.status-actions p { margin: $hmg_mini 0 0; color: $color-text; }
.status-actions.inactive { border-color: $color-warning; }
.status-confirmation { display: grid; grid-template-columns: minmax(280px, 390px) max-content; align-items: center; justify-content: end; gap: $mg_small; }
.status-confirmation .check-option { width: 100%; }
.local-loading { min-height: 100px; display: grid; place-items: center; color: $color-text; }
@media (max-width: 768px) {
  .editor-heading, .form-actions { flex-direction: column; }
  .editor-heading .btn, .form-actions .btn { width: 100%; }
  .form-grid, .responsibility-grid { grid-template-columns: 1fr; }
  .permission-modes, .permission-dual-list { grid-template-columns: 1fr; max-width: none; }
  .permission-transfer-indicator { transform: rotate(90deg); }
  .permission-transfer-list { min-height: 220px; }
  .permission-column header { align-items: flex-start; flex-direction: column; }
  .permission-column header .btn { width: 100%; }
  .permission-row.selected { align-items: flex-start; flex-direction: column; }
  .permission-home { width: 100%; justify-content: flex-start; padding: 0; }
  .span-2, .reason-field { grid-column: auto; }
  .status-actions { grid-template-columns: 1fr; }
  .status-confirmation { grid-template-columns: 1fr; align-items: stretch; justify-content: stretch; }
  .status-confirmation .btn { width: 100%; }
}
@media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; } }
</style>
